import type React from "react";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Campsite, PriceCheckResponse, PricesResponse } from "@/types/api";
import type {
  BillingInfo,
  BookingState,
  CarInfo,
  LoadingState,
  SelectedExtra,
  Traveller,
} from "@/types/booking";
import { filterExtrasForCampsite } from "@/utils/booking";
import { logError } from "@/utils/logError";
import { useAppConfig, useCampground } from "./AppConfigContext";

const STORAGE_KEY = "campnerd_booking_state";

interface BookingContextValue {
  state: BookingState;
  loadingStates: {
    price: LoadingState;
  };
  actions: {
    setToken: (token: string | null) => void;
    setDraftBookingToken: (token: string | null) => void;
    setDateFrom: (date: Date | null) => void;
    setDateUntil: (date: Date | null) => void;
    setDisplayedDate: (date: Date | null) => void;
    setSelectedPersons: (persons: Record<number, number>) => void;
    setSelectedCampsiteType: (type: string | null) => void;
    setSelectedCampsite: (campsite: Campsite | null) => void;
    setSelectedExtras: (extras: SelectedExtra[]) => void;
    setExtra: (extra: SelectedExtra) => void;
    removeExtra: (extraId: number) => void;
    setBilling: (key: keyof BillingInfo, value: unknown) => void;
    setCar: (key: keyof CarInfo, value: unknown) => void;
    setTraveller: (index: number, key: keyof Traveller, value: unknown) => void;
    setPrice: (price: PriceCheckResponse) => void;
    resetPrice: () => void;
    setPrices: (prices: PricesResponse) => void;
    resetPrices: () => void;
    setDetailsValid: (valid: boolean) => void;
    setBookingId: (id: string | null) => void;
    setBookingNumber: (number: string | null) => void;
    setBookingCreatedAt: (timestamp: string | null) => void;
    setPriceLoading: (loading: boolean, error?: Error | null) => void;
    resetBooking: () => void;
  };
}

const initialBookingState: BookingState = {
  token: null,
  draftBookingToken: null,
  dateFrom: null,
  dateUntil: null,
  displayedDate: null,
  selectedPersons: {},
  selectedCampsiteType: null,
  selectedCampsite: null,
  selectedExtras: [],
  billing: {
    name: "",
    lastname: "",
    mail: "",
    phone: "",
    street: "",
    street_additional: "",
    zip: "",
    city: "",
    country: { code: "" },
    message: "",
  },
  car: {
    license_plate: "",
    length: undefined,
    width: undefined,
  },
  travellers: {},
  price: null,
  prices: null,
  detailsValid: false,
  bookingId: null,
  bookingNumber: null,
  bookingCreatedAt: null,
};

const initialLoadingStates: {
  price: LoadingState;
} = {
  price: { loading: false, error: null },
};

const BookingContext = createContext<BookingContextValue | undefined>(undefined);

export function BookingProvider({ children }: { children: React.ReactNode }) {
  const { campgroundSlug } = useAppConfig();
  const { data: campgroundData } = useCampground();
  const isInitialized = useRef(false);
  const isSyncingStorage = useRef(false);
  const prevSlugRef = useRef(campgroundSlug);

  const getInitialState = (): BookingState => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      isInitialized.current = true;
      return initialBookingState;
    }

    try {
      const parsed = JSON.parse(stored);

      // parse dates
      parsed.dateFrom = parsed.dateFrom ? new Date(parsed.dateFrom) : null;
      parsed.dateUntil = parsed.dateUntil ? new Date(parsed.dateUntil) : null;
      parsed.displayedDate = parsed.displayedDate ? new Date(parsed.displayedDate) : null;

      isInitialized.current = true;
      return { ...initialBookingState, ...parsed };
    } catch (error) {
      logError(error);
      isInitialized.current = true;
      return initialBookingState;
    }
  };

  const [state, setState] = useState<BookingState>(getInitialState);
  const [loadingStates, setLoadingStates] = useState(initialLoadingStates);

  useEffect(() => {
    if (prevSlugRef.current !== campgroundSlug) {
      prevSlugRef.current = campgroundSlug;
      sessionStorage.removeItem(STORAGE_KEY);
      setState(initialBookingState);
    }
  }, [campgroundSlug]);

  const actions = useMemo(
    () => ({
      setToken: (token: string | null) => setState((s) => ({ ...s, token })),
      setDraftBookingToken: (token: string | null) =>
        setState((s) => ({ ...s, draftBookingToken: token })),
      setDateFrom: (date: Date | null) => setState((s) => ({ ...s, dateFrom: date })),
      setDateUntil: (date: Date | null) => setState((s) => ({ ...s, dateUntil: date })),
      setDisplayedDate: (date: Date | null) => setState((s) => ({ ...s, displayedDate: date })),
      setSelectedPersons: (persons: Record<number, number>) =>
        setState((s) => ({
          ...s,
          selectedPersons: persons,
          detailsValid: false,
        })),
      setSelectedCampsiteType: (type: string | null) =>
        setState((s) => ({ ...s, selectedCampsiteType: type })),
      setSelectedCampsite: (campsite: Campsite | null) =>
        setState((s) => {
          if (!campsite) return { ...s, selectedCampsite: null, price: null, prices: null };

          // Filter to extras valid for the new campsite type
          const filteredExtras = filterExtrasForCampsite(
            s.selectedExtras,
            campgroundData.campground.extras,
            campsite.campsite_type,
          );
          return {
            ...s,
            selectedCampsite: campsite,
            selectedExtras: filteredExtras,
            price: null,
            prices: null,
          };
        }),
      setSelectedExtras: (extras: SelectedExtra[]) =>
        setState((s) => ({ ...s, selectedExtras: extras })),
      setExtra: (extra: SelectedExtra) =>
        setState((s) => {
          const existing = s.selectedExtras.find((e) => e.id === extra.id);
          if (existing) {
            return {
              ...s,
              selectedExtras: s.selectedExtras.map((e) => (e.id === extra.id ? extra : e)),
            };
          }
          return {
            ...s,
            selectedExtras: [...s.selectedExtras, extra],
          };
        }),
      removeExtra: (extraId: number) =>
        setState((s) => ({
          ...s,
          selectedExtras: s.selectedExtras.filter((e) => e.id !== extraId),
        })),
      setBilling: (key: keyof BillingInfo, value: unknown) =>
        setState((s) => ({
          ...s,
          billing: { ...s.billing, [key]: value },
        })),
      setCar: (key: keyof CarInfo, value: unknown) =>
        setState((s) => ({
          ...s,
          car: { ...s.car, [key]: value },
        })),
      setTraveller: (index: number, key: keyof Traveller, value: unknown) =>
        setState((s) => ({
          ...s,
          travellers: {
            ...s.travellers,
            [index]: {
              ...s.travellers[index],
              [key]: value,
            },
          },
        })),
      setPrice: (price: PriceCheckResponse) => setState((s) => ({ ...s, price })),
      resetPrice: () => setState((s) => ({ ...s, price: initialBookingState.price })),
      setPrices: (prices: PricesResponse) => setState((s) => ({ ...s, prices })),
      resetPrices: () => setState((s) => ({ ...s, prices: initialBookingState.prices })),
      setDetailsValid: (valid: boolean) => setState((s) => ({ ...s, detailsValid: valid })),
      setBookingId: (id: string | null) => setState((s) => ({ ...s, bookingId: id })),
      setBookingNumber: (number: string | null) =>
        setState((s) => ({ ...s, bookingNumber: number })),
      setBookingCreatedAt: (timestamp: string | null) =>
        setState((s) => ({ ...s, bookingCreatedAt: timestamp })),
      setPriceLoading: (loading: boolean, error?: Error | null) =>
        setLoadingStates((s) => ({
          ...s,
          price: { loading, error: error || null },
        })),
      resetBooking: () => {
        sessionStorage.removeItem(STORAGE_KEY);
        setState(initialBookingState);
      },
    }),
    [campgroundData.campground.extras],
  );

  useEffect(() => {
    if (!isInitialized.current || isSyncingStorage.current) return;

    isSyncingStorage.current = true;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      logError(error);
    } finally {
      isSyncingStorage.current = false;
    }
  }, [state]);

  const value = useMemo(() => ({ state, loadingStates, actions }), [state, loadingStates, actions]);

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export function useBookingContext() {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error("useBookingContext must be used within BookingProvider");
  }
  return context;
}
